import * as React from "react";
import * as ReactDOM from "react-dom";
import { getMorticians } from "../../../api/persons";
import { IPerson } from "../../../models/get";
import { Loader, LoaderStyle } from "../../../components/loader";
import { ToolBar, ToolBarItem, ToolBarSeparator } from "../../../components/toolbar";
import { InputSize } from "../../../components/input";
import { TextBox } from "../../../components/textBox";
import { Button, ButtonType, ButtonSize } from "../../../components/button";
import { Table, CheckColumn, ITableColumn, ITableRowEventArgs } from "../../../components/table";
import SettingsNav from "../nav";
import Dialog from "./dialog";

interface IProperties
{
}

interface IState
{
    /**
     * Gibt an, ob gerade geladen wird.
     */
    isLoading?: boolean,

    /**
     * Gibt an, ob der Dialog geöffnet ist.
     */
    isDialogOpen?: boolean,

    /**
     * Gibt an, ob der Dialog zum bearbeiten eines Datensatzes geöffnet wird.
     */
    isDialogInEditMode?: boolean,

    /**
     * Enthält Text der zum Filtern der Liste verwendet wird.
     */
    filterValue?: string,

    /**
     * Enthält die geladenen Daten.
     */
    morticians?: IPerson[],

    /**
     * Enthält die gefilterten Daten.
     */
    filteredMorticians?: IPerson[],

    /**
     * Enthält den momentan selektierten Datensatz.
     */
    selectedItem?: IPerson,

    /**
     * Enthält den momentan selektierten Zeilenindex.
     */
    selectedRowIndex?: string
}

export class MorticianSettings extends React.Component<IProperties, IState>
{
    /**
     * Erstellt eine neue Instanz der Klasse @see MorticiansList.
     * @param props Alle gesetzten Eigenschaften.
     */
    constructor(props: IProperties)
    {
        super(props);

        this.state = {
            isLoading: true
        }

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        var morticians = await getMorticians(false);

        this.setState({
            isLoading: false,
            morticians: morticians,
            filteredMorticians: morticians
        });
    }

    /**
     * Aktualisiert die Liste.
     */
    private async refreshAsync()
    {
        this.setState({
            isLoading: true,
            filterValue: null
        });

        await this.initAsync(this.props);
    }

    /**
     * Wird ausgeführt, sobald sich der Text der Filter TextBox ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     */
    private onFilterTextChanged = (value: any[], originalValue: string[]): void =>
    {
        const { morticians } = this.state;
        var filter: string = value[0];
        var filteredData: IPerson[] = [];

        if (filter != null && filter !== "")
            filteredData = morticians.where(s => (s.firstname && s.firstname.toLowerCase().indexOf(filter.toLowerCase()) >= 0) || (s.lastname && s.lastname.toLowerCase().indexOf(filter.toLowerCase()) >= 0));
        else
            filteredData = morticians;

        this.setState({
            filterValue: value[0],
            filteredMorticians: filteredData
        });
    }

    /**
     * Wird ausgeführt sobald auf eine Zeile geklickt wird. Die Daten der ausgewählten
     * Zeile werden hier für weitere Zwecke zwischengespeichert.
     * @param e
     */
    private onRowClick = (e: ITableRowEventArgs<any>) =>
    {
        this.setState({
            selectedItem: e.dataSource,
            selectedRowIndex: e.index
        });
    }

    /**
     * Öffnet den Dialog zum erstellen eines neuen Datensatzes.
     */
    private onNewClick = () =>
    {
        this.setState({
            isDialogOpen: true,
            isDialogInEditMode: false
        });
    }

    /**
     * Öffnet den Dialog zum bearbeiten eines Datensatzes.
     */
    private onEditClick = () =>
    {
        this.setState({
            isDialogOpen: true,
            isDialogInEditMode: true
        });
    }

    /**
     * Wird aufgerufen, sobald sich der Dialog schließt.
     * @param refresh Angabe, ob die Ansicht aktualisiert werden soll.
     */
    private onDialogClosed = async (refresh: boolean) =>
    {
        if (refresh)
        {
            await this.refreshAsync();
            this.setState({
                isDialogOpen: false,
                selectedItem: null,
                selectedRowIndex: null
            });
        }
        else
        {
            this.setState({
                isDialogOpen: false
            });
        }
    }

    /**
     * Render einen InfoText wenn die Liste leer ist.
     */
    private renderInfoText(): JSX.Element
    {
        return (
            <div className="settings-default_empty">
                <i className="settings-default_empty_icon mdi mdi-worker"></i>
                <div className="settings-default_empty_header">Bestatter</div>
                <div
                    style={{
                        width: 370
                    }}
                    className="settings-default_empty_text">
                    In Terminen ist es möglich Bestatter auszuwählen. Erstellen Sie einen oder mehrere um eine Auswahlmöglichkeit zu haben.
                </div>
                <Button
                    icon="plus"
                    text="Bestatter"
                    type={ButtonType.primary}
                    size={ButtonSize.big}
                    onClick={this.onNewClick} />
            </div>
        );
    }

    /**
     * Rendert die Tabelle.
     */
    private renderTable(): JSX.Element
    {
        const { morticians, filteredMorticians } = this.state;
        const columns: ITableColumn[] = [
            {
                header: "Nachname",
                accessor: "lastname",
                width: 200
            },
            {
                header: "Vorname",
                accessor: "firstname",
                width: 200
            },
            {
                header: "Straße",
                accessor: "street",
                width: 200
            },
            {
                header: "Hausnummer",
                accessor: "street_no",
                width: 200
            },
            {
                header: "PLZ",
                accessor: "zip_code",
                width: 200
            },
            {
                header: "City",
                accessor: "city",
                width: 200
            },
            {
                header: "Aktivkennzeichen",
                cell: (person: IPerson) => <CheckColumn isChecked={person.contractor_is_active} width={100} />,
                width: 200
            }
        ];

        if (morticians != null && morticians.length > 0)
        {
            return (
                <Table
                    style={{
                        position: "relative"
                    }}
                    dataSource={filteredMorticians}
                    columns={columns}
                    keyAccessor="id"
                    onRowClick={this.onRowClick} />
            );
        }
        else
        {
            return this.renderInfoText();
        }
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const { isLoading, isDialogOpen, isDialogInEditMode, filterValue, morticians, selectedItem } = this.state;
        const isListEmpty: boolean = morticians == null || morticians.length === 0;
        const isItemSelected: boolean = selectedItem != null;

        if (isLoading)
        {
            return (
                <Loader
                    style={{ marginTop: 40 }}
                    loaderStyle={LoaderStyle.style1}
                    infoText="Bestatter werden geladen" />
            );
        }
        
        return (
            <div className="settings-content">
                <div className="settings-content_container">
                    <div className="settings-content_nav">
                        <SettingsNav selected="morticians" />
                    </div>
                    <div className="settings-default-container">
                        {
                            !isListEmpty &&
                            <div className="settings-default_toolbar">
                                <ToolBar hasTopBorder={false}>
                                    <ToolBarItem
                                        icon="plus"
                                        text="Neu"
                                        onClick={this.onNewClick} />
                                    <ToolBarSeparator />
                                    <ToolBarItem
                                        icon="pencil"
                                        text="Bearbeiten"
                                        onClick={this.onEditClick}
                                        isDisabled={!isItemSelected} />
                                </ToolBar>
                            </div>
                        }
                        {
                            !isListEmpty &&
                            <div className="settings-default_filter">
                                <TextBox
                                    placeholder="Filter Nachname und Vorname..."
                                    size={InputSize.normal}
                                    value={filterValue}
                                    onTextChange={this.onFilterTextChanged} />
                            </div>
                        }
                        <div className="settings-default">
                            {this.renderTable()}
                        </div>
                        {
                            isDialogOpen &&
                            <Dialog
                                morticianId={isDialogInEditMode && isItemSelected && selectedItem.id}
                                onClose={this.onDialogClosed} />
                        }
                        </div>
                </div>
            </div>
        );
    }
}